import type { ReactNode } from "react";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * The chip Platform Settings → General puts on a menu item — New, Updated or
 * Coming Soon — as the menu panel draws it: the sidebar, the phone's menu
 * sheet and the Platform / Company Settings rail. One component, so the three
 * cannot drift (the rail drew no chip at all until 2026-10-20).
 *
 * Drawn for the deep panel, not with `Badge`: Badge's tints are measured on
 * the page surface, and on the panel its Coming Soon read as grey text and its
 * New as a light-theme green (Andrew, 2026-10-20: "look terrible"). The
 * colours live in index.css (`.menu-chip`), keyed off `data-kind`.
 *
 * Coming Soon says only "Soon" here. The full words took ~75px of a ~146px
 * label, so "Availability", "Signing Queue" and "Sales Management" were cut
 * to "Availabi…" — the chip outranked the name it labels.
 */
export type MenuChipKind = "new" | "updated" | "coming_soon";

export function menuChipKind(chip: string | null | undefined): MenuChipKind | null {
  return chip === "new" || chip === "updated" || chip === "coming_soon" ? chip : null;
}

export function MenuChip({ chip }: { chip: string | null | undefined }) {
  const t = useTranslation();
  const kind = menuChipKind(chip);
  if (!kind) return null;
  const label =
    kind === "new" ? t("new", "New") : kind === "updated" ? t("updated", "Updated") : t("menuChipSoon", "Soon");
  return (
    <span className="menu-chip" data-kind={kind} title={kind === "coming_soon" ? t("comingSoonChip", "Coming Soon") : undefined}>
      {label}
    </span>
  );
}

/**
 * A menu row's name with its chip. Neither gives way to the other: the chip
 * always sits at the trailing edge of the row, on the name's line (Andrew,
 * 2026-10-20: "never wrap the chip"), and the name is never truncated to make
 * room for it — a name too long to share the line wraps onto a second line
 * of its own ("Sales / Management  Soon"), as a long translation does.
 */
export function MenuRowLabel({ title, chip, count }: { title: ReactNode; chip: string | null | undefined; count?: ReactNode }) {
  // The count (what is waiting there) sits against the name, never at the
  // far edge (Andrew, 2026-10-26: "butted up against the menu name").
  // A name too long for the row wraps to a second line, never truncates: the
  // docked sidebar is 210px on an iPad in portrait, and "Company settings"
  // beside its external-link glyph, or a longer translation, would otherwise
  // read "Company sett…".
  if (!menuChipKind(chip)) {
    if (!count) return <span className="min-w-0 flex-1 break-words py-1.5 leading-snug">{title}</span>;
    return (
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="min-w-0 break-words py-1.5 leading-snug">{title}</span>
        {count}
      </span>
    );
  }
  return (
    // gap-1.5 is only the least space between them (the chip is pushed to the
    // trailing edge): 8px wrapped "Competitor Analysis" in a 256px rail by
    // a fraction of a pixel.
    <span className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5">
      <span className={count ? "min-w-0 break-words leading-snug" : "min-w-0 flex-1 break-words leading-snug"}>{title}</span>
      {count}
      {count ? <span className="ml-auto"><MenuChip chip={chip} /></span> : <MenuChip chip={chip} />}
    </span>
  );
}
